import { computeHalfTrimWidth } from '../../helpers/page-trim-width'
import { computeBleedLabelLayout } from '../helpers/bleed-label-layout'
import type { BleedMetrics } from '../types/bleed-metrics'
import { BleedBandLabel } from './bleed-band-label'
import { PrintBleedTints } from './print-bleed-tints'

type PrintBleedTintOverlayProps = BleedMetrics & {
  /**
   * Show outer bleed ring and in-trim labels. Bindery-gutter (outer trim tint) is in `PrintBleedTints`
   * when `centerGap` is set; trim bridges and copy stay in `SpineGutterOverlay` in the document.
   */
  visible?: boolean
  /** Below shape/text layers (z≥2); ruler guides use z≈20 and stay above. */
  zIndex?: number
  /** When true, only one page is visible (no spine/gutter). */
  isSinglePage?: boolean
  /** Which side the single page occupies ('left' or 'right'). Only used when isSinglePage is true. */
  singlePageSide?: 'left' | 'right'
  /**
   * Bindery gutter (double spread). Full-bleed-height in the print-bleed overlay; stacks with outer
   * top/bottom in spine *x* only.
   */
  centerGap?: number
  /** See {@link PrintBleedTints} — gates every yellow band and its in-trim/outer-bleed labels,
   * fed from the editor's "Show Bleed Bar" toggle. */
  showOutsideTrim?: boolean
}

/**
 * Outer bleed ring + in-trim labels. When `centerGap` is set, the bindery-gutter fill is in
 * `PrintBleedTints` (not in `SpineGutterOverlay`, which only carries trim bridges and copy).
 */
export function PrintBleedTintOverlay({
  bleedWidth,
  bleedHeight,
  bleedMargin,
  trimHeight,
  visible: showBleedRing = true,
  trimWidth,
  zIndex: overlayZIndex = 1,
  isSinglePage = false,
  singlePageSide,
  centerGap = 0,
  showOutsideTrim = true,
}: PrintBleedTintOverlayProps) {
  if (!showBleedRing) {
    return null
  }

  const pageTrimWidth = computeHalfTrimWidth(trimWidth)
  const halfSpreadWidth = Math.floor(bleedWidth / 2)
  /**
   * Single-page clip window: the trim's own spine-side edge to the trim's outer (fore-edge) side
   * plus one real `bleedMargin`. Deliberately does NOT add a margin past the spine-side edge —
   * that 12pt zone is a virtual "no facing page here" gap, not a real bleed edge, and is already
   * given its own correct treatment by `SinglePageSpineBleedStrip` (an unclipped sibling of this
   * overlay in `SinglePageBleedOverlayAboveShapes`, not a descendant of it). Including it here too
   * let `PrintBleedTints`' full-width "always-on" safety band — which spans the whole double-page
   * width in document space, relying entirely on this clip to hide the half belonging to the
   * non-existent facing page — bleed 12pt past the dashed trim line on the spine side, confirmed
   * live via devtools.
   */
  const clipLeft = isSinglePage ? (singlePageSide === 'right' ? halfSpreadWidth : 0) : 0
  const clipWidth = isSinglePage ? pageTrimWidth + bleedMargin : bleedWidth
  const labels = showOutsideTrim
    ? computeBleedLabelLayout({
        bleedWidth,
        bleedHeight,
        bleedMargin,
        trimWidth,
        trimHeight,
        isSinglePage,
        singlePageSide,
      })
    : []

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        zIndex: overlayZIndex,
        // `clip-path: inset()`, not the legacy `clip: rect()` this replaced — `clip` is deprecated
        // and, like several other CSS positioning tricks this session, unreliable inside a
        // `transform: scale()` ancestor (this whole tree sits inside `documentCanvasRef`'s scaled
        // tree).
        ...(isSinglePage
          ? {
              clipPath: `inset(0px ${Math.max(0, bleedWidth - (clipLeft + clipWidth))}px 0px ${clipLeft}px)`,
            }
          : {}),
      }}
    >
      <PrintBleedTints
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        bleedMargin={bleedMargin}
        trimWidth={trimWidth}
        trimHeight={trimHeight}
        tintZIndex={1}
        showHint={false}
        centerGap={isSinglePage ? 0 : centerGap}
        showOutsideTrim={showOutsideTrim}
      />
      {labels.map(({ key, ...label }) => (
        <BleedBandLabel key={key} {...label} />
      ))}
    </div>
  )
}
